import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from "@mui/material";
import { RiCalendarLine, RiCopperCoinLine, RiTimeLine, RiVidiconLine } from "@remixicon/react";
import {
  getBookingById,
  getChildNames,
  getLessonRecords,
  getLatestTutorRatings,
  getPayCycleVersions,
  getUsersByIds,
} from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { whenLabel } from "../../utils/queuePresentation";
import { childKey, childPath } from "../../utils/lessonPresentation";
import { personName, sessionStatusChip } from "../../utils/studentDetail";
import { creditTile, lengthTile, lessonShortId, lessonTitle, subjectText, timeTile } from "../../utils/lessonDetail";
import { tokens } from "../../utils/figmaTokens";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import LinkTabs from "../../components/kit/LinkTabs";
import StatusChip from "../../components/kit/StatusChip";
import { LessonMenu } from "./LessonActions";
import { ActivityTab, ParticipantsTab, PaymentsTab, RatingTab } from "./LessonSections";

// One lesson (047 D92, D99–D103), drawn from Figma's lesson page (the Completed frames on
// 11431:74050): the head with its three tiles and ⋯, then four tabs, the tab in the link
// (`?tab=`) so it can be shared. It replaces the Lessons page's drawer; `/lessons?lesson=<id>`
// lands here (Lessons.js). VIEW_LESSONS opens it (App.js); the doors keep their own roles.

const TABS = [
  { value: "participants", label: "Participants" },
  { value: "activity", label: "Activity" },
  { value: "payments", label: "Payments" },
  { value: "rating", label: "Rating & Feedback" },
];

// The reader's zone, named on the time tile ("Cairo time").
const readerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

// The head's three tiles (D99): a ringed icon, the value, and a small caption.
const HeadTiles = ({ tiles }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1px", bgcolor: tokens.border.default, border: `1px solid ${tokens.border.default}`, borderRadius: "12px", overflow: "hidden" }}>
    {tiles.map(({ key, Icon, value, caption }) => (
      <Box key={key} sx={{ flex: "1 1 200px", minWidth: 0, display: "flex", alignItems: "center", gap: "12px", px: "16px", py: "14px", bgcolor: tokens.surface.default }}>
        <Box aria-hidden sx={{ display: "flex", p: "10px", borderRadius: "999px", bgcolor: tokens.brand.subtle, color: tokens.icon.brand }}>
          <Icon size={20} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="titleMetric" component="div" sx={{ color: tokens.text.primary }}>
            {value}
          </Typography>
          <Typography variant="labelMicro" component="div" sx={{ color: tokens.text.tertiary, textTransform: "uppercase" }}>
            {caption}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const LessonPage = () => {
  const { lessonId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === searchParams.get("tab")) ? searchParams.get("tab") : "participants";
  const { user } = useAuth();
  const can = {
    students: hasPermission(user, permissions.VIEW_STUDENTS),
    tutors: hasPermission(user, permissions.VIEW_TUTORS),
  };
  const timeZone = readerZone();

  const [booking, setBooking] = useState(null);
  const [missing, setMissing] = useState(false);
  const [records, setRecords] = useState({ loading: true, error: "", walletRows: [], tickets: [], ratings: [] });
  const [users, setUsers] = useState({});
  const [childName, setChildName] = useState(null);
  const [versions, setVersions] = useState(null);
  const [tutorRatings, setTutorRatings] = useState({ loading: true, error: "", list: [] });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const bookingToken = useRef(0);
  const recordsToken = useRef(0);

  const notify = (message, severity = "success") => setSnack({ open: true, message, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // The newest read wins. A refresh that fails keeps what is on screen; only a first read can end
  // in "Lesson not found." (getBookingById answers null rather than throwing).
  const load = useCallback(async () => {
    const token = ++bookingToken.current;
    const found = await getBookingById(lessonId);
    if (token !== bookingToken.current) return;
    if (found) setBooking(found);
    setMissing(!found);
  }, [lessonId]);

  const loadRecords = useCallback(async () => {
    const token = ++recordsToken.current;
    setRecords((r) => ({ ...r, loading: true, error: "" }));
    try {
      const found = await getLessonRecords(lessonId);
      if (token === recordsToken.current) setRecords({ loading: false, error: "", ...found });
    } catch (error) {
      console.error("Error loading the lesson's records:", error);
      if (token === recordsToken.current) setRecords((r) => ({ ...r, loading: false, error: "Nothing has been changed." }));
    }
  }, [lessonId]);

  useEffect(() => {
    setBooking(null);
    setMissing(false);
    setUsers({});
    setChildName(null);
    load();
    loadRecords();
  }, [load, loadRecords]);

  useEffect(() => {
    getPayCycleVersions().then(setVersions);
  }, []);

  // The people the lesson names, read again with every read of it, so a credit shows at once.
  const { walletRows, ratings } = records;
  useEffect(() => {
    if (!booking) return undefined;
    let cancelled = false;
    const ids = [booking.studentId, booking.teacherId, booking.cancelledBy, booking.reportedBy, booking.finishedBy];
    getUsersByIds([...ids, ...ratings.map((r) => r.ratedBy), ...walletRows.map((r) => r.userId)])
      .then((found) => !cancelled && setUsers(found))
      .catch((error) => console.error("Error loading people:", error));
    const pair = childPath(booking);
    if (pair) getChildNames([pair]).then((names) => !cancelled && setChildName(names[childKey(booking)] || null));
    return () => {
      cancelled = true;
    };
  }, [booking, ratings, walletRows]);

  // The tutor's latest ratings, for the Rating & Feedback tab only: four, so three are from other
  // lessons even when this lesson's is among them.
  const teacherId = booking?.teacherId;
  const loadTutorRatings = useCallback(async () => {
    if (!teacherId) return;
    setTutorRatings({ loading: true, error: "", list: [] });
    try {
      setTutorRatings({ loading: false, error: "", list: await getLatestTutorRatings(teacherId) });
    } catch (error) {
      console.error("Error loading the tutor's ratings:", error);
      setTutorRatings({ loading: false, error: "Nothing has been changed.", list: [] });
    }
  }, [teacherId]);
  useEffect(() => {
    if (tab === "rating") loadTutorRatings();
  }, [tab, loadTutorRatings]);

  // The top bar's "Support / Lessons / #a1B2c3D4"; "Not found" once the id is.
  useBreadcrumbName(lessonId, booking ? lessonShortId(booking.id) : missing ? "Not found" : null);

  if (!booking) {
    return missing ? (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          Lesson not found.
        </Alert>
        <Button component={RouterLink} to="/lessons" sx={{ mt: 2 }}>
          Back to Lessons
        </Button>
      </Box>
    ) : (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const nameOf = (uid) => personName(users[uid]) || undefined;
  const parent = personName(users[booking.studentId]);
  const learner = childPath(booking) ? childName || "Child" : parent || undefined;
  const label = [subjectText(booking.subject) || "Lesson", learner, whenLabel(booking.startTimeUTC, { timeZone })].filter(Boolean).join(" · ");

  // After a door: say what happened, then read the lesson and its records again.
  const afterAction = (message, severity) => {
    if (message) notify(message, severity);
    if (severity === "error") return;
    load();
    loadRecords();
  };

  const base = `/lessons/${booking.id}`;
  const tabs = TABS.map((t) => ({ ...t, to: t.value === "participants" ? base : `${base}?tab=${t.value}` }));
  const shared = { booking, records, users, nameOf, timeZone, can, onRetry: loadRecords };

  return (
    <Box
      sx={{
        bgcolor: tokens.surface.default,
        border: `1px solid ${tokens.border.subtle}`,
        borderRadius: "16px",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box
        sx={{
          bgcolor: tokens.surface.sunken,
          border: `1px solid ${tokens.border.default}`,
          borderRadius: "16px",
          p: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
          <Box
            aria-hidden
            sx={{
              width: 60,
              height: 60,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              bgcolor: tokens.surface.default,
              border: `1px solid ${tokens.border.default}`,
              color: tokens.icon.default,
            }}
          >
            <RiVidiconLine size={28} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography variant="titleMedium" component="h1" sx={{ color: tokens.text.primary, overflowWrap: "anywhere" }}>
              {lessonTitle(booking)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <Typography variant="bodyMedium" component="span" sx={{ color: tokens.text.secondary }}>
                {lessonShortId(booking.id)}
              </Typography>
              <StatusChip {...sessionStatusChip(booking)} />
            </Box>
          </Box>
          <LessonMenu head booking={booking} label={label} personName={parent || booking.studentId} actingUser={user} onDone={afterAction} />
        </Box>
        <HeadTiles
          tiles={[
            { key: "time", Icon: RiCalendarLine, ...timeTile(booking, { timeZone }) },
            { key: "length", Icon: RiTimeLine, ...lengthTile(booking) },
            { key: "credit", Icon: RiCopperCoinLine, ...creditTile(booking) },
          ]}
        />
      </Box>

      <LinkTabs value={tab} tabs={tabs} label="Lesson sections" />

      {tab === "participants" && <ParticipantsTab {...shared} childName={childName} />}
      {tab === "activity" && <ActivityTab {...shared} learner={learner} />}
      {tab === "payments" && <PaymentsTab {...shared} versions={versions} />}
      {tab === "rating" && <RatingTab {...shared} childName={childName} tutorRatings={tutorRatings} onRetryOthers={loadTutorRatings} />}

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LessonPage;
